<template>
  <div>
    <!-- Desktop/Tablet Table View -->
    <div v-if="calculations.length > 0" class="hidden md:block overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th scope="col" :class="headerCellClass">
              <Translation name="dashboard.facility" default="Facility" />
            </th>
            <th scope="col" :class="headerCellClass">
              <Translation name="dashboard.calculation_id" default="ID" />
            </th>
            <th scope="col" :class="headerCellClass">
              <Translation name="dashboard.location" default="Location" />
            </th>
            <th scope="col" :class="headerCellClass">
              <Translation
                name="dashboard.payroll_year"
                default="Payroll Year"
              />
            </th>
            <th v-if="!legacy" scope="col" :class="progressHeaderClass">
              <Translation name="dashboard.progress" default="Progress" />
            </th>
            <th scope="col" :class="headerCellClass">
              <Translation name="dashboard.status" default="Status" />
            </th>
            <th scope="col" :class="headerCellClass">
              <Translation
                name="dashboard.last_updated"
                default="Last Updated"
              />
            </th>
            <th v-if="!legacy" scope="col" class="relative px-6 py-3">
              <span class="sr-only">
                <Translation name="dashboard.actions" default="Actions" />
              </span>
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr
            v-for="calculation in calculations"
            :key="calculation.id"
            class="hover:bg-[#F4FCFB] cursor-pointer"
            @click="handleRowClick(calculation.id)"
          >
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="flex items-center">
                <div>
                  <div class="text-sm font-semibold text-gray-900">
                    {{ getFacilityName(calculation) }}
                  </div>
                  <div class="text-sm text-gray-500">
                    {{ getFacilityId(calculation) }}
                  </div>
                </div>
              </div>
            </td>

            <td class="px-6 py-4 whitespace-nowrap">
              <div class="text-sm text-gray-600">
                {{ calculation.id }}
              </div>
            </td>

            <td class="px-6 py-4 whitespace-nowrap">
              <div class="flex">
                <div
                  v-if="getCountryCode(calculation)"
                  class="mr-1 flex-shrink-0"
                >
                  <span
                    :class="`fi fi-${getCountryCode(
                      calculation
                    ).toLowerCase()}`"
                    class="rounded-full bg-white border border-gray-200 flex items-center justify-center overflow-hidden shadow-sm"
                    style="
                      font-size: 16px;
                      width: 16px;
                      height: 16px;
                      min-width: 16px;
                      min-height: 16px;
                    "
                  ></span>
                </div>
                <span class="text-sm text-gray-blue">{{
                  getCountryName(calculation)
                }}</span>
              </div>
            </td>

            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
              {{ getPayrollYear(calculation) }}
            </td>

            <td v-if="!legacy" class="px-2 py-4 whitespace-nowrap">
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div
                  class="h-2 rounded-full bg-success-300"
                  :style="{
                    width:
                      getProgressPercentage(calculation.frontend_status) + '%',
                  }"
                ></div>
              </div>
            </td>

            <td class="px-6 py-4 whitespace-nowrap">
              <div class="flex items-center gap-2">
                <span
                  class="px-2.5 py-0.5 inline-flex text-sm font-medium rounded-full"
                  :class="getStatusClass(calculation.frontend_status)"
                >
                  {{ getStatusLabel(calculation.frontend_status) }}
                </span>
                <span
                  v-if="calculation.test"
                  class="px-2.5 py-0.5 inline-flex text-sm font-medium rounded-full bg-amber-100 text-amber-800"
                >
                  {{ testLabel }}
                </span>
              </div>
            </td>

            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
              {{ getLastUpdated(calculation) }}
            </td>

            <td
              v-if="!legacy"
              class="px-6 py-4 whitespace-nowrap text-sm font-medium"
              :class="dropdownPositioning === 'advanced' ? 'text-right' : ''"
            >
              <div
                :class="[
                  'flex space-x-2',
                  dropdownPositioning === 'advanced'
                    ? 'relative dropdown-container'
                    : 'relative',
                ]"
              >
                <button
                  :ref="
                    dropdownPositioning === 'advanced'
                      ? (el) => setDropdownButtonRef(calculation.id, el)
                      : undefined
                  "
                  @click.stop="toggleDropdown(calculation.id, $event)"
                  :class="
                    dropdownPositioning === 'advanced'
                      ? 'text-gray-400 hover:text-gray-600'
                      : 'p-1 rounded-md focus:outline-none'
                  "
                >
                  <DotsVerticalIcon />
                </button>

                <!-- Advanced Dropdown (Teleported to body) -->
                <Teleport v-if="dropdownPositioning === 'advanced'" to="body">
                  <div
                    v-if="isDropdownOpen(calculation.id)"
                    :style="getDropdownStyles(calculation.id)"
                    class="fixed w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10"
                  >
                    <button
                      v-for="action in getActions(calculation)"
                      :key="action.name"
                      @click="handleAction(action.name, calculation.id)"
                      :disabled="action.disabled"
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span class="mr-3">
                        <component :is="action.icon" />
                      </span>
                      <Translation v-bind.prop="action.label" />
                    </button>
                  </div>
                </Teleport>

                <!-- Simple Dropdown (positioned relative to button) -->
                <div
                  v-if="
                    dropdownPositioning === 'simple' &&
                    openDropdownId === calculation.id
                  "
                  :class="[
                    'absolute right-0 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10',
                    isLastRow(calculation.id) ? 'bottom-8 mb-2' : 'top-8 mt-2',
                  ]"
                  @click.stop
                >
                  <div class="py-1">
                    <button
                      v-for="action in getActions(calculation)"
                      :key="action.name"
                      @click="handleAction(action.name, calculation.id)"
                      :disabled="action.disabled"
                      class="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <component :is="action.icon" class="mr-3 h-4 w-4" />
                      {{ action.label }}
                    </button>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Mobile Card View -->
    <div v-if="calculations.length > 0" class="md:hidden space-y-4">
      <div
        v-for="calculation in calculations"
        :key="calculation.id"
        @click="handleRowClick(calculation.id)"
        class="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
      >
        <!-- Facility Name, ID and Action Icon -->
        <div class="flex items-start justify-between mb-3">
          <div class="flex-1">
            <div class="text-base font-semibold text-gray-900 mb-1">
              {{ getFacilityName(calculation) }}
            </div>
            <div class="text-sm text-gray-500">
              {{ getFacilityId(calculation) }}
            </div>
          </div>
          <div
            v-if="!legacy"
            class="relative dropdown-container ml-3 flex-shrink-0"
          >
            <button
              :ref="(el) => setDropdownButtonRef(calculation.id, el)"
              @click.stop="toggleDropdown(calculation.id, $event)"
              class="text-gray-400 hover:text-gray-600"
            >
              <DotsVerticalIcon />
            </button>

            <!-- Dropdown Menu - Teleported to body -->
            <Teleport to="body">
              <div
                v-if="isDropdownOpen(calculation.id)"
                :style="getDropdownStyles(calculation.id)"
                class="fixed w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10"
              >
                <button
                  v-for="action in getActions(calculation)"
                  :key="action.name"
                  @click="handleAction(action.name, calculation.id)"
                  :disabled="action.disabled"
                  class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span class="mr-3">
                    <component :is="action.icon" />
                  </span>
                  <Translation v-bind.prop="action.label" />
                </button>
              </div>
            </Teleport>
          </div>
        </div>

        <!-- Location and Year -->
        <div class="flex flex-wrap gap-4 mb-3">
          <div class="flex items-center">
            <div
              v-if="getCountryCode(calculation)"
              class="mr-1.5 flex-shrink-0"
            >
              <span
                :class="`fi fi-${getCountryCode(calculation).toLowerCase()}`"
                class="rounded-full bg-white border border-gray-200 flex items-center justify-center overflow-hidden shadow-sm"
                style="
                  font-size: 16px;
                  width: 16px;
                  height: 16px;
                  min-width: 16px;
                  min-height: 16px;
                "
              ></span>
            </div>
            <span class="text-sm text-gray-blue">{{
              getCountryName(calculation)
            }}</span>
          </div>
          <div class="text-sm text-gray-600">
            <span class="text-gray-500"
              ><Translation
                name="dashboard.payroll_year"
                default="Payroll Year"
            /></span>
            {{ getPayrollYear(calculation) }}
          </div>
        </div>

        <!-- Progress Bar -->
        <div class="mb-3">
          <div class="text-xs text-gray-500 mb-1.5">
            <Translation name="dashboard.progress" default="Progress" />
          </div>
          <div class="w-full bg-gray-200 rounded-full h-2">
            <div
              class="h-2 rounded-full bg-success-300"
              :style="{
                width: getProgressPercentage(calculation.frontend_status) + '%',
              }"
            ></div>
          </div>
        </div>

        <!-- Status and Last Updated -->
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <span
              class="px-2.5 py-0.5 inline-flex text-sm font-medium rounded-full"
              :class="getStatusClass(calculation.frontend_status)"
            >
              {{ getStatusLabel(calculation.frontend_status) }}
            </span>
            <span
              v-if="calculation.test"
              class="px-2.5 py-0.5 inline-flex text-sm font-medium rounded-full bg-amber-100 text-amber-800"
            >
              {{ testLabel }}
            </span>
          </div>
          <span class="text-sm text-gray-600">
            {{ getLastUpdated(calculation) }}
          </span>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-if="calculations.length === 0"
      class="text-center py-12 text-gray-500"
    >
      <slot name="empty-state">
        <Translation name="no_calculations" default="No calculations found" />
      </slot>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, Teleport } from "vue";
import DotsVerticalIcon from "./icons/DotsVerticalIcon.vue";
import EditIcon from "./icons/EditIcon.vue";
import TrashIcon from "./icons/TrashIcon.vue";
import CopyIcon from "./icons/CopyIcon.vue";
import { useTranslation } from "./aft/useTranslations";
import Translation from "./aft/Translation.vue";

export default {
  name: "LivingWageCalculationsTable",
  components: { DotsVerticalIcon, Teleport, EditIcon, TrashIcon, Translation },
  props: {
    legacy: {
      type: Boolean,
      default: false,
    },
    calculations: {
      type: Array,
      required: true,
    },
    locale: {
      type: String,
      required: true,
    },
    archiveUrlTemplate: {
      type: String,
      required: false,
    },
    copyUrlTemplate: {
      type: String,
      required: false,
    },
    reportUrlTemplate: {
      type: String,
      required: false,
    },
    wizardContinueUrlTemplate: {
      type: String,
      required: false,
    },
    wizardFirstStepUrlTemplate: {
      type: String,
      required: false,
    },
    reopenUrlTemplate: {
      type: String,
      required: false,
    },
    dropdownPositioning: {
      type: String,
      default: "advanced", // 'advanced' (with Teleport) or 'simple'
      validator: (value) => ["advanced", "simple"].includes(value),
    },
  },
  emits: [],
  setup(props) {
    const openDropdownId = ref(null);
    const dropdownPosition = ref({});
    const dropdownButtonRefs = ref({});
    const isArchiving = ref(false);
    const { _t } = useTranslation();

    // Computed classes
    const headerCellClass = computed(() => {
      return "px-6 py-3 xl:py-4 text-left text-xs font-normal text-gray-500";
    });

    const progressHeaderClass = computed(() => {
      return "pl-2 pr-10 py-3 xl:py-4 text-left text-xs font-normal text-gray-500";
    });

    const isArchived = (calculation) =>
      calculation.frontend_status[0] === "archived";

    // Table actions configuration - returns actions based on calculation status
    const getActions = (calculation) => {
      const archiveOrRestore = isArchived(calculation)
        ? {
            name: "reopen",
            label: { name: "dashboard.restore", default: "Restore" },
          }
        : {
            name: "archive",
            label: { name: "dashboard.archive", default: "Archive" },
          };
      return [
        {
          name: "edit",
          label: isFinalized(calculation)
            ? { name: "dashboard.reopen_to_edit", default: "Reopen to Edit" }
            : { name: "dashboard.edit", default: "Edit" },
          icon: EditIcon,
          disabled: false,
        },
        {
          name: "copy",
          label: {
            name: "dashboard.copy_calculation",
            default: "Copy Calculation",
          },
          icon: CopyIcon,
          disabled: false,
        },
        {
          name: archiveOrRestore.name,
          label: archiveOrRestore.label,
          icon: TrashIcon,
          disabled: isArchiving.value,
        },
      ];
    };

    // Data accessor methods (always use nested structure)
    const getFacilityName = (calculation) => {
      return calculation.facility?.name || "";
    };

    const getFacilityId = (calculation) => {
      return calculation.facility?.facility_id || "";
    };

    const getCountryCode = (calculation) => {
      return calculation.facility?.country || "";
    };

    const getCountryName = (calculation) => {
      return calculation.facility?.country_name || "";
    };

    const getPayrollYear = (calculation) => {
      return calculation.reporting_year || "";
    };

    const getLastUpdated = (calculation) => {
      return formatDate(calculation.updated_at);
    };

    // Date formatting (for nested structure)
    const formatDate = (dateString) => {
      if (!dateString) return "";

      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return _t("dashboard.today", "Today").value;
      } else if (diffDays === 1) {
        return _t("dashboard.yesterday", "1 day ago").value;
      } else if (diffDays < 7) {
        return `${diffDays} ${_t("dashboard.days_ago", "days ago").value}`;
      } else if (diffDays < 14) {
        return _t("dashboard.one_week_ago", "1 week ago").value;
      } else if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return `${weeks} ${_t("dashboard.weeks_ago", "weeks ago").value}`;
      } else if (diffDays < 60) {
        return _t("dashboard.one_month_ago", "1 month ago").value;
      } else {
        const months = Math.floor(diffDays / 30);
        return `${months} ${_t("dashboard.months_ago", "months ago").value}`;
      }
    };

    // Status and progress methods
    const getStatusClass = (status) => {
      const classMap = {
        created: "bg-gray-100 text-gray-800",
        basic_info_completed: "bg-warning-200 text-warning-900",
        benchmark_completed: "bg-warning-200 text-warning-900",
        payroll_completed: "bg-warning-200 text-warning-900",
        draft: "bg-warning-200 text-warning-900", // frontend_status grouping
        submitted: "bg-success-200 text-green-800",
        in_audit: "bg-blue-200 text-blue-900",
        archived: "bg-gray-300 text-gray-600",
      };
      return classMap[status[0]] || "bg-gray-100 text-gray-800";
    };

    const getProgressPercentage = (status) => {
      const progressMap = {
        created: 0,
        basic_info_completed: 25,
        benchmark_completed: 50,
        payroll_completed: 75,
        draft: 50, // frontend_status grouping (average of draft statuses)
        submitted: 100,
        in_audit: 100,
        archived: 100,
      };
      return progressMap[status[0]] || 0;
    };

    const getStatusLabel = (status) => {
      const labelMap = {
        created: _t("dashboard.status_created", "Created").value,
        basic_info_completed: _t(
          "dashboard.status_basic_info_completed",
          "Basic Info Completed"
        ).value,
        benchmark_completed: _t(
          "dashboard.status_benchmark_completed",
          "Benchmark Completed"
        ).value,
        payroll_completed: _t(
          "dashboard.status_payroll_completed",
          "Payroll Completed"
        ).value,
        draft: _t("dashboard.status_draft", "Draft").value, // frontend_status grouping
        submitted: _t("dashboard.status_submitted", "Submitted").value,
        in_audit: _t("dashboard.status_in_audit", "In Audit").value,
        archived: _t("dashboard.status_archived", "Archived").value,
      };
      return labelMap[status[1]] || status;
    };

    const testLabel = _t("dashboard.status_test", "Test");

    // Dropdown methods
    const setDropdownButtonRef = (calculationId, el) => {
      if (el) {
        dropdownButtonRefs.value[calculationId] = el;
      }
    };

    const toggleDropdown = (calculationId, event) => {
      if (openDropdownId.value === calculationId) {
        openDropdownId.value = null;
        dropdownPosition.value = {};
      } else {
        openDropdownId.value = calculationId;

        if (props.dropdownPositioning === "advanced" && event) {
          // Calculate if dropdown should open upward
          const button = event.currentTarget;
          const rect = button.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const dropdownHeight = 100; // Approximate height of dropdown
          const spaceBelow = viewportHeight - rect.bottom;

          // Store position info for this dropdown
          dropdownPosition.value[calculationId] = {
            top: rect.bottom,
            right: window.innerWidth - rect.right,
            openUpward: spaceBelow < dropdownHeight,
            buttonBottom: rect.bottom,
            buttonTop: rect.top,
          };
        }
      }
    };

    const closeDropdown = () => {
      openDropdownId.value = null;
      dropdownPosition.value = {};
    };

    const isDropdownOpen = (calculationId) => {
      return openDropdownId.value === calculationId;
    };

    const getDropdownStyles = (calculationId) => {
      const position = dropdownPosition.value[calculationId];
      if (!position) return {};

      const styles = {
        right: `${position.right}px`,
      };

      if (position.openUpward) {
        // Open upward
        styles.bottom = `${window.innerHeight - position.buttonTop + 8}px`;
      } else {
        // Open downward
        styles.top = `${position.buttonBottom + 8}px`;
      }

      return styles;
    };

    const isLastRow = (calculationId) => {
      const currentIndex = props.calculations.findIndex(
        (c) => c.id === calculationId
      );
      return currentIndex === props.calculations.length - 1;
    };

    // Helper to check if calculation is in a finalized status (submitted or in_audit)
    const isFinalized = (calculation) => {
      return (
        calculation.frontend_status[0] === "submitted" ||
        calculation.frontend_status[0] === "in_audit"
      );
    };

    // Event handlers
    // Row click: submitted/in_audit -> report page, otherwise -> wizard continue (where user left off)
    const handleRowClick = (calculationId) => {
      const calculation = props.calculations.find(
        (c) => c.id === calculationId
      );

      if (calculation && (props.legacy || isFinalized(calculation))) {
        const reportUrl = props.reportUrlTemplate.replace(
          "__ID__",
          calculationId
        );
        window.location.href = reportUrl;
      } else {
        const wizardUrl = props.wizardContinueUrlTemplate.replace(
          "__ID__",
          calculationId
        );
        window.location.href = wizardUrl;
      }
    };

    // Edit action: always go to wizard first step (basic info)
    // For finalized calculations (submitted/in_audit), call reopen endpoint first
    const handleAction = async (actionName, calculationId) => {
      closeDropdown();

      if (actionName === "edit") {
        const calculation = props.calculations.find(
          (c) => c.id === calculationId
        );

        if (calculation && isFinalized(calculation)) {
          // For finalized calculations, submit form to reopen endpoint (follows redirect)
          const reopenUrl = props.reopenUrlTemplate.replace(
            "__ID__",
            calculationId
          );

          const form = document.createElement("form");
          form.method = "POST";
          form.action = reopenUrl;

          const methodInput = document.createElement("input");
          methodInput.type = "hidden";
          methodInput.name = "_method";
          methodInput.value = "PATCH";
          form.appendChild(methodInput);

          const csrfToken = document.querySelector(
            'meta[name="csrf-token"]'
          ).content;
          const tokenInput = document.createElement("input");
          tokenInput.type = "hidden";
          tokenInput.name = "authenticity_token";
          tokenInput.value = csrfToken;
          form.appendChild(tokenInput);

          document.body.appendChild(form);
          form.submit();
        } else {
          const wizardUrl = props.wizardFirstStepUrlTemplate.replace(
            "__ID__",
            calculationId
          );
          window.location.href = wizardUrl;
        }
      } else if (actionName === "copy") {
        const calculation = props.calculations.find(
          (c) => c.id === calculationId
        );

        if (calculation) {
          const copyUrl = props.copyUrlTemplate.replace(
            "__ID__",
            calculationId
          );

          const form = document.createElement("form");
          form.method = "POST";
          form.action = copyUrl;

          const methodInput = document.createElement("input");
          methodInput.type = "hidden";
          methodInput.name = "_method";
          methodInput.value = "PATCH";
          form.appendChild(methodInput);

          const csrfToken = document.querySelector(
            'meta[name="csrf-token"]'
          ).content;
          const tokenInput = document.createElement("input");
          tokenInput.type = "hidden";
          tokenInput.name = "authenticity_token";
          tokenInput.value = csrfToken;
          form.appendChild(tokenInput);

          document.body.appendChild(form);
          form.submit();
        }
      } else if (actionName === "reopen") {
        const calculation = props.calculations.find(
          (c) => c.id === calculationId
        );
        if (
          !calculation ||
          !confirm(
            _t(
              "dashboard.confirm_restore",
              "Are you sure you want to restore this calculation?"
            ).value
          )
        ) {
          return;
        }
        const reopenUrl = props.reopenUrlTemplate.replace(
          "__ID__",
          calculationId
        );
        const form = document.createElement("form");
        form.method = "POST";
        form.action = reopenUrl;
        const methodInput = document.createElement("input");
        methodInput.type = "hidden";
        methodInput.name = "_method";
        methodInput.value = "PATCH";
        form.appendChild(methodInput);
        const csrfToken = document.querySelector(
          'meta[name="csrf-token"]'
        ).content;
        const tokenInput = document.createElement("input");
        tokenInput.type = "hidden";
        tokenInput.name = "authenticity_token";
        tokenInput.value = csrfToken;
        form.appendChild(tokenInput);
        document.body.appendChild(form);
        form.submit();
      } else if (actionName === "archive") {
        if (isArchiving.value) return;

        if (
          !confirm(
            _t(
              "dashboard.confirm_archive",
              "Are you sure you want to archive this calculation?"
            ).value
          )
        ) {
          return;
        }

        isArchiving.value = true;
        const archiveUrl = props.archiveUrlTemplate.replace(
          "__ID__",
          calculationId
        );

        try {
          const response = await fetch(archiveUrl, {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
                .content,
            },
          });

          const data = await response.json();

          if (data.success) {
            alert(
              _t(
                "wage_calculations.archived_successfully",
                "Calculation archived successfully"
              ).value
            );
            window.location.reload();
          } else {
            alert(
              data.message ||
                _t("dashboard.archive_error", "Failed to archive calculation")
                  .value
            );
          }
        } catch (error) {
          console.error("Archive error:", error);
          alert(
            _t("dashboard.archive_error", "Failed to archive calculation").value
          );
        } finally {
          isArchiving.value = false;
        }
      }
    };

    // Click outside handler
    const handleClickOutside = (event) => {
      if (openDropdownId.value !== null) {
        const dropdownElements = document.querySelectorAll(
          ".dropdown-container"
        );
        let clickedInside = false;

        dropdownElements.forEach((element) => {
          if (element.contains(event.target)) {
            clickedInside = true;
          }
        });

        if (!clickedInside) {
          closeDropdown();
        }
      }
    };

    const handleScrollOrResize = () => {
      if (openDropdownId.value !== null) {
        closeDropdown();
      }
    };

    // Lifecycle hooks
    onMounted(() => {
      document.addEventListener("click", handleClickOutside);
      window.addEventListener("scroll", handleScrollOrResize, true);
      window.addEventListener("resize", handleScrollOrResize);
    });

    onUnmounted(() => {
      document.removeEventListener("click", handleClickOutside);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    });

    return {
      openDropdownId,
      headerCellClass,
      progressHeaderClass,
      getActions,
      getFacilityName,
      getFacilityId,
      getCountryCode,
      getCountryName,
      getPayrollYear,
      getLastUpdated,
      getStatusClass,
      getProgressPercentage,
      getStatusLabel,
      testLabel,
      setDropdownButtonRef,
      toggleDropdown,
      isDropdownOpen,
      getDropdownStyles,
      isLastRow,
      handleRowClick,
      handleAction,
    };
  },
};
</script>
